import { isIpfsGatewayUrl } from 'utils/ipfs'

/** Requests for one IPFS gateway resource: the first try and two retries. */
export const GATEWAY_ATTEMPTS = 3

/**
 * Wait before the retry that follows `attempt` (0-based): 0.5-1.5 s, then
 * 1-3 s. The jitter keeps the cards of a grid from retrying together.
 */
export const retryDelayMs = (
  attempt: number,
  random: () => number = Math.random
): number => 2 ** attempt * 1000 * (0.5 + random())

/**
 * Adds a `retry` query to an IPFS gateway URL after the first attempt, so a
 * cached 429 is not served again. Other URLs are returned as they are.
 */
export const withRetryParam = (url: string, attempt: number): string => {
  if (attempt === 0 || !isIpfsGatewayUrl(url)) {
    return url
  }
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}retry=${attempt}`
}

const isRetryableStatus = (status: number): boolean =>
  status === 429 || status >= 500

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Fetches a URL. An IPFS gateway URL that answers 429, a 5xx status or a
 * network error is requested again, up to GATEWAY_ATTEMPTS times.
 * Returns the last response; throws the last network error.
 */
export const fetchWithGatewayRetry = async (
  url: string,
  wait: (ms: number) => Promise<void> = sleep
): Promise<Response> => {
  const attempts = isIpfsGatewayUrl(url) ? GATEWAY_ATTEMPTS : 1
  for (let attempt = 0; ; attempt += 1) {
    const isLast = attempt === attempts - 1
    try {
      const response = await fetch(withRetryParam(url, attempt))
      if (isLast || !isRetryableStatus(response.status)) {
        return response
      }
    } catch (error) {
      if (isLast) {
        throw error
      }
    }
    await wait(retryDelayMs(attempt))
  }
}
